import { Fragment, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getAdminRedemptions,
  reverseRedemption,
  logAuditEvent,
  AdminRedemption,
  AdminRedemptionGolfer,
  AdminRedemptionSort,
} from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";
import { HUBSPOT_IDS } from "../../../utils/hubspotIds";
import AccessCodeLogs from "./AccessCodeLogs";

type ReverseState =
  | { status: "idle" }
  | { status: "confirm"; redemption: AdminRedemption; golfer: AdminRedemptionGolfer }
  | { status: "processing"; redemption: AdminRedemption; golfer: AdminRedemptionGolfer }
  | { status: "error"; redemption: AdminRedemption; golfer: AdminRedemptionGolfer; message: string };

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  // Eastern time, as HubSpot shows Redeemed At, whatever time zone the admin's computer is in
  return d.toLocaleString(undefined, {
    timeZone: "America/New_York",
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { timeZone: "America/New_York", month: "short", day: "2-digit", year: "numeric" });
};

// Redemptions imported from the old system only have a date, stored as exactly noon Eastern
// (12:00:00.000). Show those as a date; every redemption made in the app has its real time.
const formatRedeemedAt = (iso: string) => {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime())) return formatDateTime(iso);
  const dateOnly =
    d.getUTCMilliseconds() === 0 &&
    d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hourCycle: "h23" }) === "12:00:00";
  return dateOnly ? formatDate(iso) : formatDateTime(iso);
};

const BRAND_GREEN = "#023822";

const thStyle: React.CSSProperties = { padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap", background: "#f8f9fa" };

// Columns that sort, with the direction a first click uses
const COLUMNS: { key: AdminRedemptionSort; label: string; firstDir: "asc" | "desc" }[] = [
  { key: "firstName",     label: "First Name",    firstDir: "asc" },
  { key: "lastName",      label: "Last Name",     firstDir: "asc" },
  { key: "email",         label: "Email",         firstDir: "asc" },
  { key: "vipCardNumber", label: "VIP Card #",    firstDir: "asc" },
  { key: "count",         label: "Redemptions",   firstDir: "desc" },
  { key: "lastRedeemed",  label: "Last Redeemed", firstDir: "desc" },
];

// The page's two views; ?view=codes links straight to the access code log
const ViewTabs = ({ view, onChange }: { view: "redemptions" | "codes"; onChange: (v: "redemptions" | "codes") => void }) => (
  <div className="d-inline-flex flex-wrap mb-3 p-1" role="tablist" style={{ background: "#f1f3f5", borderRadius: 10, gap: 4 }}>
    {([["redemptions", "Redemptions", "bi-ticket-perforated"], ["codes", "Access code log", "bi-key"]] as const).map(([key, label, icon]) => (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={view === key}
        onClick={() => onChange(key)}
        style={{ border: "none", borderRadius: 8, padding: "0.35rem 0.9rem", fontSize: "0.82rem", fontWeight: 600, whiteSpace: "nowrap", backgroundColor: view === key ? "#fff" : "transparent", color: view === key ? BRAND_GREEN : "#6c757d", boxShadow: view === key ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}
      >
        <i className={`bi ${icon} me-1`} />{label}
      </button>
    ))}
  </div>
);

const AllRedemptions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "codes" ? "codes" : "redemptions";
  const setView = (v: "redemptions" | "codes") => setSearchParams(v === "codes" ? { view: "codes" } : {}, { replace: true });

  const [golfers,          setGolfers]          = useState<AdminRedemptionGolfer[]>([]);
  const [totalContacts,    setTotalContacts]    = useState(0);
  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [updatedAt,        setUpdatedAt]        = useState("");
  const [loading,          setLoading]          = useState(false);
  const [refreshing,       setRefreshing]       = useState(false);
  const [error,            setError]            = useState<string | null>(null);

  // Filters. year "" = the current season (the server decides it from Annual Dates)
  const [searchInput, setSearchInput] = useState("");
  const [search,      setSearch]      = useState("");
  const [year,        setYear]        = useState("");
  const [years,       setYears]       = useState<string[]>([]);
  const [startDate,   setStartDate]   = useState("");
  const [endDate,     setEndDate]     = useState("");

  const [sort, setSort] = useState<AdminRedemptionSort>("lastRedeemed");
  const [dir,  setDir]  = useState<"asc" | "desc">("desc");

  const [pageSize,    setPageSize]    = useState<20 | 50 | 100>(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Which golfer rows are expanded (by contactId, or card number when there's no contact)
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [reverseState, setReverseState] = useState<ReverseState>({ status: "idle" });

  // Ignore responses from requests that a newer one has replaced
  const requestId = useRef(0);

  const fetchRedemptions = async (refresh = false) => {
    const id = ++requestId.current;
    setLoading(true);
    setRefreshing(refresh);
    setError(null);
    try {
      const resp = await getAdminRedemptions({ page: currentPage, pageSize, sort, dir, year, q: search, startDate, endDate, refresh });
      if (id !== requestId.current) return;
      if (resp.success) {
        setGolfers(resp.contacts);
        setTotalContacts(resp.totalContacts);
        setTotalRedemptions(resp.totalRedemptions);
        setUpdatedAt(resp.updatedAt);
        setYears(resp.years || []);
        if (!year && resp.year) setYear(resp.year);
      } else {
        setError(resp.message || "Failed to load redemptions.");
        setGolfers([]);
      }
    } catch (err: any) {
      if (id !== requestId.current) return;
      setError(err?.response?.data?.message || "Network error. Please try again.");
      setGolfers([]);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Wait for typing to pause before searching
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setCurrentPage(1); }, [search, year, startDate, endDate, pageSize, sort, dir]);

  useEffect(() => {
    if (view === "redemptions") fetchRedemptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, currentPage, pageSize, sort, dir, year, search, startDate, endDate]);

  const golferKey = (g: AdminRedemptionGolfer) => g.contactId || `card:${g.vipCardNumber}`;

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sortBy = (key: AdminRedemptionSort, firstDir: "asc" | "desc") => {
    if (sort === key) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDir(firstDir);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalContacts / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const firstShown = totalContacts === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const lastShown  = (safePage - 1) * pageSize + golfers.length;

  const cancelReverse = () => setReverseState({ status: "idle" });

  const confirmReverse = async () => {
    if (reverseState.status !== "confirm") return;
    const { redemption, golfer } = reverseState;
    setReverseState({ status: "processing", redemption, golfer });

    try {
      const resp = await reverseRedemption(redemption.id);
      if (resp.success) {
        const name = `${golfer.firstName} ${golfer.lastName}`.trim() || golfer.contactId;
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "redemption_reversal",
          label:   "Redemption Reversed",
          details: `${name} — "${redemption.offer}"`,
        });
        setReverseState({ status: "idle" });
        fetchRedemptions();
      } else {
        setReverseState({ status: "error", redemption, golfer, message: resp.message || "Failed to reverse redemption." });
      }
    } catch (err: any) {
      setReverseState({
        status: "error",
        redemption,
        golfer,
        message: err?.response?.data?.message || err?.message || "Network error.",
      });
    }
  };

  const modalOpen = reverseState.status !== "idle";
  const confirmingOffer = reverseState.status !== "idle" ? reverseState.redemption.offer : "";
  const hasDates = !!(startDate || endDate);

  if (view === "codes") {
    return (
      <div>
        <ViewTabs view={view} onChange={setView} />
        <AccessCodeLogs />
      </div>
    );
  }

  return (
    <div>
      <ViewTabs view={view} onChange={setView} />

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: "#023822", letterSpacing: "-0.3px" }}>All Redemptions</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>{totalContacts.toLocaleString()}</span> member{totalContacts !== 1 ? "s" : ""}{" · "}
            <span style={{ fontWeight: 600, color: "#495057" }}>{totalRedemptions.toLocaleString()}</span> redemption{totalRedemptions !== 1 ? "s" : ""}
            {updatedAt && <>{" · "}updated {formatDateTime(updatedAt)}</>}
          </p>
        </div>
        <button
          onClick={() => fetchRedemptions(true)}
          disabled={loading}
          title="Reload every redemption from HubSpot"
          style={{ backgroundColor: "#023822", color: "#fff", border: "none", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500 }}
        >
          <i className="bi bi-arrow-clockwise me-1" />{refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters row */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div style={{ flex: "1 1 240px", maxWidth: 380 }}>
            <label className="form-label small text-muted mb-1" htmlFor="redemption-search">Search</label>
            <input
              id="redemption-search"
              type="text"
              className="form-control form-control-sm"
              placeholder="Name, email or VIP card #…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </div>
          <div>
            <label className="form-label small text-muted mb-1" htmlFor="redemption-year">Year</label>
            <select
              id="redemption-year"
              className="form-select form-select-sm"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={{ borderRadius: 8, minWidth: 110 }}
            >
              {!year && <option value="">Current</option>}
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
              <option value="all">All years</option>
            </select>
          </div>
          <div>
            <label className="form-label small text-muted mb-1" htmlFor="redemption-from">From</label>
            <input
              id="redemption-from"
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </div>
          <div>
            <label className="form-label small text-muted mb-1" htmlFor="redemption-to">To</label>
            <input
              id="redemption-to"
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ borderRadius: 8 }}
            />
          </div>
          {hasDates && (
            <button
              type="button"
              className="btn btn-sm btn-link text-decoration-none px-1"
              style={{ color: BRAND_GREEN }}
              onClick={() => { setStartDate(""); setEndDate(""); }}
            >
              Clear dates
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      {!error && (
        <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div className="table-responsive">
          <table
            className="table table-hover align-middle mb-0"
            style={{ fontSize: "0.84rem" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                <th style={{ ...thStyle, width: 32 }} />
                <th style={{ ...thStyle, width: 36, paddingLeft: 0, paddingRight: 0 }} />
                {COLUMNS.map((col) => {
                  const active = sort === col.key;
                  return (
                    <th
                      key={col.key}
                      style={{ ...thStyle, cursor: "pointer", userSelect: "none", color: active ? BRAND_GREEN : thStyle.color }}
                      onClick={() => sortBy(col.key, col.firstDir)}
                      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
                    >
                      {col.label}
                      <i
                        className={`bi ${active ? (dir === "asc" ? "bi-caret-up-fill" : "bi-caret-down-fill") : "bi-chevron-expand"} ms-1`}
                        style={{ opacity: active ? 1 : 0.4, fontSize: "0.7rem" }}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    {refreshing ? "Reloading every redemption from HubSpot… this can take up to a minute." : "Loading redemptions…"}
                  </td>
                </tr>
              )}
              {!loading && golfers.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-4 text-muted">
                    {search ? "No members match your search." : "No redemptions found for these filters."}
                  </td>
                </tr>
              )}
              {!loading &&
                golfers.map((g) => {
                  const key = golferKey(g);
                  const isOpen = expanded.has(key);
                  return (
                    <Fragment key={key}>
                      {/* Golfer summary row */}
                      <tr onClick={() => toggleExpanded(key)} style={{ cursor: "pointer", userSelect: "none" }}>
                        <td className="text-center">
                          <span
                            style={{
                              display: "inline-block",
                              transition: "transform 0.15s",
                              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                              color: BRAND_GREEN,
                            }}
                          >
                            ▾
                          </span>
                        </td>
                        <td className="text-center px-0">
                          {g.contactId && (
                            <button
                              type="button"
                              className="btn btn-sm p-0 border-0"
                              title="View this golfer's offers page (opens a preview in a new tab)"
                              aria-label={`View offers as ${`${g.firstName} ${g.lastName}`.trim() || "this golfer"}`}
                              style={{ color: BRAND_GREEN, fontSize: "1rem", lineHeight: 1 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/offers?previewContact=${encodeURIComponent(g.contactId)}`, "_blank");
                              }}
                            >
                              <i className="bi bi-eye" />
                            </button>
                          )}
                        </td>
                        <td>{g.firstName || "—"}</td>
                        <td>{g.lastName || "—"}</td>
                        <td>
                          {g.email ? (
                            <a
                              href={`mailto:${g.email}`}
                              className="text-decoration-none"
                              style={{ color: BRAND_GREEN }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {g.email}
                            </a>
                          ) : "—"}
                        </td>
                        <td>
                          <span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{g.vipCardNumber || "—"}</span>
                        </td>
                        <td>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "0.2rem 0.6rem",
                              borderRadius: 20,
                              fontSize: "0.72rem",
                              fontWeight: 600,
                              backgroundColor: "rgba(25,135,84,0.1)",
                              color: "#146c43",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {g.count}
                          </span>
                        </td>
                        <td className="text-nowrap text-muted">{formatRedeemedAt(g.lastRedeemedAt)}</td>
                      </tr>

                      {/* Expanded redemption rows */}
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0 }}>
                            <div
                              style={{
                                backgroundColor: "#f8f9fa",
                                borderLeft: `3px solid ${BRAND_GREEN}`,
                                padding: "0.5rem 1rem 0.75rem 2rem",
                              }}
                            >
                              <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.82rem" }}>
                                <thead>
                                  <tr>
                                    <th className="text-nowrap bg-transparent border-0">Offer</th>
                                    <th className="text-nowrap bg-transparent border-0">Club ID</th>
                                    <th className="text-nowrap bg-transparent border-0">Offer #</th>
                                    <th className="text-nowrap bg-transparent border-0">Redeemed At</th>
                                    <th className="bg-transparent border-0" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {g.redemptions.map((r) => {
                                    const isReversing = reverseState.status === "processing" && reverseState.redemption.id === r.id;
                                    return (
                                      <tr key={r.id}>
                                        <td style={{ maxWidth: 260 }}>
                                          <span className="text-truncate d-block" style={{ maxWidth: 260 }} title={r.offer}>
                                            {r.offer || "—"}
                                          </span>
                                        </td>
                                        <td>{r.clubId || "—"}</td>
                                        <td>
                                          <span style={{ fontFamily: "monospace" }}>{r.offerNumber || "—"}</span>
                                        </td>
                                        <td className="text-nowrap text-muted">{formatRedeemedAt(r.redeemedAt || r.createDate)}</td>
                                        <td className="text-end text-nowrap">
                                          {r.pipelineStage === HUBSPOT_IDS.redemptionReversedStage ? (
                                            <span style={{ fontSize: "0.72rem", color: "#6c757d", fontStyle: "italic" }}>Reversed</span>
                                          ) : (
                                            <button
                                              type="button"
                                              title="Reverse redemption"
                                              disabled={isReversing}
                                              style={{ backgroundColor: "rgba(220,53,69,0.08)", border: "none", color: "#b02a37", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.65rem", borderRadius: 6 }}
                                              onClick={() => setReverseState({ status: "confirm", redemption: r, golfer: g })}
                                            >
                                              {isReversing ? <span className="spinner-border spinner-border-sm" role="status" /> : "✕"}
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Pagination */}
      {!error && totalContacts > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="small text-muted">Rows per page:</span>
            {([20, 50, 100] as const).map((n) => (
              <button key={n} type="button" className="btn btn-sm"
                style={{ minWidth: 40, backgroundColor: pageSize === n ? BRAND_GREEN : "transparent", color: pageSize === n ? "#fff" : "#495057", border: `1px solid ${pageSize === n ? BRAND_GREEN : "#dee2e6"}`, fontWeight: pageSize === n ? 600 : 400 }}
                onClick={() => setPageSize(n)}>{n}</button>
            ))}
            <span className="small text-muted ms-2">
              {firstShown.toLocaleString()}–{lastShown.toLocaleString()} of {totalContacts.toLocaleString()}
            </span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === 1} onClick={() => setCurrentPage(1)}>«</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => { if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…"); acc.push(p); return acc; }, [])
              .map((p, i) => p === "…"
                ? <span key={`e-${i}`} className="px-1 small text-muted">…</span>
                : <button key={p} type="button" className="btn btn-sm" disabled={loading}
                    style={{ minWidth: 34, backgroundColor: p === safePage ? BRAND_GREEN : "transparent", color: p === safePage ? "#fff" : "#495057", border: `1px solid ${p === safePage ? BRAND_GREEN : "#dee2e6"}`, fontWeight: p === safePage ? 600 : 400 }}
                    onClick={() => setCurrentPage(p as number)}>{p}</button>
              )}
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>›</button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={loading || safePage === totalPages} onClick={() => setCurrentPage(totalPages)}>»</button>
          </div>
        </div>
      )}

      {/* Reverse confirm modal */}
      {modalOpen && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={reverseState.status === "processing" ? undefined : cancelReverse}
          />

          {/* Dialog */}
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 420, zIndex: 1050, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              {/* Modal header */}
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: BRAND_GREEN,
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                  Reverse Redemption
                </span>
                {reverseState.status !== "processing" && (
                  <button
                    type="button"
                    className="btn-close btn-close-white btn-sm"
                    onClick={cancelReverse}
                  />
                )}
              </div>

              <div className="card-body p-4">
                {/* Offer preview */}
                {confirmingOffer && (
                  <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                    <div className="small text-muted mb-1">Offer</div>
                    <div className="small fw-semibold" style={{ color: BRAND_GREEN }}>
                      {confirmingOffer}
                    </div>
                  </div>
                )}

                {/* Error */}
                {reverseState.status === "error" && (
                  <div className="text-center py-2">
                    <div className="text-danger fw-semibold mb-2">Reversal failed</div>
                    <div className="small text-muted mb-3">{reverseState.message}</div>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={cancelReverse}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setReverseState({ status: "confirm", redemption: reverseState.redemption, golfer: reverseState.golfer })}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                {/* Confirm */}
                {reverseState.status === "confirm" && (
                  <>
                    <p className="small text-muted mb-4">
                      This will reverse the redemption and restore it to the member's available
                      pool. This action cannot be undone.
                    </p>
                    <div className="d-flex gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={cancelReverse}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ backgroundColor: "#dc3545", color: "#fff", border: "none" }}
                        onClick={confirmReverse}
                      >
                        Reverse Redemption
                      </button>
                    </div>
                  </>
                )}

                {/* Processing */}
                {reverseState.status === "processing" && (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border mb-2"
                      role="status"
                      style={{ color: BRAND_GREEN }}
                    />
                    <div className="small text-muted">Processing reversal…</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AllRedemptions;
